/**
 * SoleadLab 2.0 - Google Drive Folder Import Utilities
 *
 * Pure functions for file categorization, pairing, and import orchestration.
 * No React dependencies — used by DriveImportModal.
 */

import type { DriveFile, DriveFileImportResult, DriveSrtResult, DriveExifResult } from '../services/assetApi';
import { API_BASE, importDriveFile, peekDriveFileExif } from '../services/assetApi';
import { ApiError } from '../services/apiFetch';
import type { FlightPathStats } from '../stores/projectStore';

// ── File Categories ──

export type FileCategory =
  | 'thermal_photo'
  | 'visual_photo'
  | 'zoom_photo'
  | 'video'
  | 'srt'
  | 'geotiff'
  | 'pdf'
  | 'unknown';

/** User-assigned PDF purpose */
export type PdfPurpose = 'overlay' | 'sld';

export interface CategorizedFile {
  driveFile: DriveFile;
  category: FileCategory;
  /** For PDFs, user picks overlay vs SLD. Default: overlay */
  pdfPurpose?: PdfPurpose;
  /** Whether to include in import */
  included: boolean;
  /** Flagged by `markAlreadyImported`: this file (or its photo/flight set) is
   *  already in the project. Defaulted to excluded; the user can re-include it
   *  via its checkbox or the bulk "Include anyway" action. */
  alreadyImported?: boolean;
}

/** A matched set of thermal + visual + zoom photos sharing the same base name */
export interface PhotoSet {
  baseName: string;
  /** Subfolder path the set originated from (empty string = root). e.g. "א/" */
  pathPrefix: string;
  thermal?: CategorizedFile;
  visual?: CategorizedFile;
  zoom?: CategorizedFile;
}

/** A matched flight path: thermal/visual/zoom/screen SRT+video variants merged into one */
export interface FlightPathSet {
  baseName: string;
  /** Subfolder path the set originated from (empty string = root). e.g. "א/" */
  pathPrefix: string;
  /** Thermal SRT (_T.SRT or plain .SRT) */
  thermalSrt?: CategorizedFile;
  /** Visual SRT (_V.SRT / _W.SRT) */
  visualSrt?: CategorizedFile;
  /** Zoom SRT (_Z.SRT) */
  zoomSrt?: CategorizedFile;
  /** Screen / liveview SRT (_S.SRT) */
  screenSrt?: CategorizedFile;
  /** Thermal video (_T.MP4 or plain .MP4) */
  thermalVideo?: CategorizedFile;
  /** Visual video (_V.MP4 / _W.MP4) */
  visualVideo?: CategorizedFile;
  /** Zoom video (_Z.MP4) */
  zoomVideo?: CategorizedFile;
  /** Screen / liveview video (_S.MP4) — the app's current-view recording, NOT an HD camera feed */
  screenVideo?: CategorizedFile;
}

// ── Import Progress ──

export type FileStatus = 'queued' | 'importing' | 'done' | 'error' | 'skipped';

export interface ImportFileProgress {
  file: CategorizedFile;
  status: FileStatus;
  error?: string;
  result?: DriveFileImportResult | DriveSrtResult | DriveExifResult;
}

export interface ImportProgress {
  total: number;
  completed: number;
  currentFile?: string;
  files: ImportFileProgress[];
}

// ── Categorization ──

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm']);
const GEOTIFF_EXTENSIONS = new Set(['tif', 'tiff', 'geotiff']);

/**
 * Detect file category from filename extension.
 */
export function categorizeFile(file: DriveFile): FileCategory {
  const name = file.name;
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const nameUpper = name.toUpperCase();

  // Photo variants: look for _T, _V, _W, _Z before the extension
  const basePart = nameUpper.replace(/\.[^.]+$/, ''); // Remove extension
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'heic') {
    if (basePart.endsWith('_T')) return 'thermal_photo';
    // `_V` (Matrice/Mavic visual), `_W` (H20T/H30T/M30T wide RGB), and `_D`
    // (visual camera on some modern consumer drones) are the same visual/RGB
    // pair slot.
    if (basePart.endsWith('_V') || basePart.endsWith('_W') || basePart.endsWith('_D')) return 'visual_photo';
    if (basePart.endsWith('_Z')) return 'zoom_photo';
    // Generic image without a recognized T/V/W/Z/D suffix — treat as thermal
    // (most common single-image import case). Unknown suffixes are NOT stripped
    // by photoBaseName, so such a file forms its own group (never dropped).
    return 'thermal_photo';
  }

  if (VIDEO_EXTENSIONS.has(ext)) return 'video';
  if (ext === 'srt') return 'srt';
  if (GEOTIFF_EXTENSIONS.has(ext)) return 'geotiff';
  if (ext === 'pdf') return 'pdf';

  return 'unknown';
}

/**
 * Categorize all files from a Drive folder listing.
 */
export function categorizeFiles(files: DriveFile[]): CategorizedFile[] {
  return files.map((f) => ({
    driveFile: f,
    category: categorizeFile(f),
    pdfPurpose: categorizeFile(f) === 'pdf' ? ('overlay' as PdfPurpose) : undefined,
    included: categorizeFile(f) !== 'unknown',
  }));
}

// ── Matching ──

/**
 * Extract base name for photo matching, stripping the extension and the
 * thermal/visual/zoom/wide suffix so a `_T`/`_W`/`_Z` trio (or a `_T`/`_V` pair)
 * collapses to one shared base.
 * "DJI_0001_T.JPG" → "DJI_0001"
 * "DJI_0001_W.JPG" → "DJI_0001"
 * "DJI_0001_Z.JPG" → "DJI_0001"
 *
 * Shared by the Drive matcher (`matchPhotoSets`) and the local matcher
 * (`matchLocalPhotoSets`); keep the stripped suffixes in sync with
 * `categorizeFile` / `categorizeLocalPhotoName`, which map `_V`/`_W`/`_D` → visual.
 * `_S` is intentionally NOT stripped for photos in this unit.
 */
function photoBaseName(fileName: string): string {
  const withoutExt = fileName.replace(/\.[^.]+$/, '');
  return withoutExt.replace(/_[TVZWD]$/i, '');
}

/**
 * Tolerance for treating two DJI-named files as the SAME capture when their
 * filename timestamps differ. The M4T (and similar) stamps each file of one
 * capture with its own write second, so the _T and _V of capture NNNN differ
 * by 1-2s in ~25% of captures (either order, including across a minute
 * boundary — Srikot 2026-07: `..._131759_0089_T` / `..._131801_0089_V`).
 * 10s pairs all of those while still splitting same-sequence captures from
 * different flights dumped flat into one folder (minutes apart).
 */
const PAIR_TS_TOLERANCE_MS = 10_000;

/**
 * Canonical pair-grouping key for a filename, after stripping the extension
 * and the caller's role-suffix regex.
 *
 * DJI-style bases — `<prefix><14-digit timestamp>_<sequence>` — key on
 * prefix + sequence WITHOUT the timestamp, because the timestamp is per-FILE
 * (write time), not per-CAPTURE, so it differs between the _T and _V of one
 * capture. The timestamp still participates via `ms`, letting callers split
 * same-key files that are further apart than `PAIR_TS_TOLERANCE_MS`.
 *
 * Non-DJI bases key on the full stripped base (previous behavior; `ms` null).
 * Keys are namespaced (`dji|` / `name|`) so the two forms can never collide.
 */
function pairGroupKey(fileName: string, stripRole: RegExp): { key: string; ms: number | null } {
  const base = fileName.replace(/\.[^.]+$/, '').replace(stripRole, '');
  const m = /^(.*?)(\d{14})_(\d{1,6})$/.exec(base);
  if (m) return { key: `dji|${m[1]}|${parseInt(m[3], 10)}`, ms: djiCaptureMs(m[2]) };
  return { key: `name|${base}`, ms: null };
}

/**
 * Pick the cluster a file joins within its pair-group key, or null to open a
 * new one. Scans newest-first for a cluster whose `slotTaken` is false and
 * whose capture time is within tolerance. Name-keyed files (`ms` null) only
 * ever join the MOST RECENT cluster — preserving the legacy "open group"
 * semantics where a repeated variant closes the previous group.
 */
function findPairCluster<S>(
  list: { set: S; ms: number | null }[],
  ms: number | null,
  slotTaken: (set: S) => boolean,
): { set: S; ms: number | null } | null {
  for (let i = list.length - 1; i >= 0; i--) {
    const c = list[i];
    const inTolerance = c.ms === null || ms === null || Math.abs(c.ms - ms) <= PAIR_TS_TOLERANCE_MS;
    if (!slotTaken(c.set) && inTolerance) return c;
    if (ms === null) return null; // name-keyed: most recent cluster only
  }
  return null;
}

/**
 * Roles a flight video (or its SRT sidecar) can carry, by filename suffix.
 */
export type FlightVideoRole = 'thermal' | 'visual' | 'zoom' | 'screen';

/**
 * Detect the role of a flight video or SRT sidecar by filename suffix.
 * `_V`/`_W` → visual (matches the photo mapping), `_Z` → zoom, `_S` → screen
 * (the app's current-view / liveview recording, NOT an HD inspection camera
 * feed), `_T` or no suffix → thermal (default). Unknown suffixes default to
 * thermal but are NOT stripped by `flightBaseName`, so a video with an
 * unrecognized suffix forms its OWN flight-path group (never silently dropped).
 */
export function videoRole(fileName: string): FlightVideoRole {
  const base = fileName.replace(/\.[^.]+$/, '').toUpperCase();
  if (base.endsWith('_V') || base.endsWith('_W')) return 'visual';
  if (base.endsWith('_Z')) return 'zoom';
  if (base.endsWith('_S')) return 'screen';
  // `_T` or no suffix → thermal (default)
  return 'thermal';
}

/**
 * Extract base name for SRT↔Video matching, stripping the recognized
 * thermal/visual/wide/zoom/screen suffix so a `_T`/`_W`/`_Z`/`_S` set (or a
 * `_T`/`_V` pair) collapses to one shared flight-path base.
 * "DJI_0001_T.SRT" → "DJI_0001"; "DJI_0001_S.MP4" → "DJI_0001".
 */
export function flightBaseName(fileName: string): string {
  const withoutExt = fileName.replace(/\.[^.]+$/, '');
  return withoutExt.replace(/_[TVWZS]$/i, '');
}

/**
 * Group thermal/visual/zoom photos into matched sets.
 *
 * Match key includes the subfolder path so identical filenames in different
 * subfolders (e.g. two batches each containing DJI_0001_T.JPG) produce
 * separate PhotoSets rather than collapsing into one.
 *
 * DJI-named files pair by capture SEQUENCE, not by full filename: the M4T
 * stamps the _T and _V of one capture with different write seconds, so a
 * timestamp-inclusive key orphans ~25% of pairs (see `pairGroupKey`).
 * Same-sequence files further apart than the tolerance (two flights in one
 * flat folder) still split into separate sets, as does a repeated role slot
 * (duplicate file) — nothing is dropped or mis-merged.
 */
export function matchPhotoSets(files: CategorizedFile[]): PhotoSet[] {
  const clusters = new Map<string, { set: PhotoSet; ms: number | null }[]>();
  const out: PhotoSet[] = [];

  for (const f of files) {
    if (f.category !== 'thermal_photo' && f.category !== 'visual_photo' && f.category !== 'zoom_photo') continue;

    const pathPrefix = f.driveFile.path ?? '';
    const { key: groupKey, ms } = pairGroupKey(f.driveFile.name, /_[TVZWD]$/i);
    const key = `${pathPrefix}\u0000${groupKey}`;
    const slot: 'thermal' | 'visual' | 'zoom' =
      f.category === 'thermal_photo' ? 'thermal' : f.category === 'visual_photo' ? 'visual' : 'zoom';

    let list = clusters.get(key);
    if (!list) {
      list = [];
      clusters.set(key, list);
    }

    let cluster = findPairCluster(list, ms, (s) => s[slot] !== undefined);
    if (!cluster) {
      cluster = { set: { baseName: photoBaseName(f.driveFile.name), pathPrefix }, ms };
      list.push(cluster);
      out.push(cluster.set);
    }
    cluster.set[slot] = f;
    if (cluster.ms === null) cluster.ms = ms;
  }

  return out;
}

/**
 * Group SRT files with matching video files.
 * Merges _T/_V/_W/_Z/_S variants of one capture into a single FlightPathSet by
 * shared base name (role assigned via `videoRole`).
 *
 * Match key includes the subfolder path so flights captured in different
 * batches don't get cross-paired. A video/SRT with an unrecognized suffix keeps
 * that suffix in its base name, so it forms its own set rather than being
 * dropped or mis-merged.
 */
export function matchFlightPaths(files: CategorizedFile[]): FlightPathSet[] {
  const clusters = new Map<string, { set: FlightPathSet; ms: number | null }[]>();
  const out: FlightPathSet[] = [];

  const SRT_SLOTS = { thermal: 'thermalSrt', visual: 'visualSrt', zoom: 'zoomSrt', screen: 'screenSrt' } as const;
  const VIDEO_SLOTS = { thermal: 'thermalVideo', visual: 'visualVideo', zoom: 'zoomVideo', screen: 'screenVideo' } as const;

  for (const f of files) {
    if (f.category !== 'srt' && f.category !== 'video') continue;

    const pathPrefix = f.driveFile.path ?? '';
    // Same split-second rule as photos: the M4T stamps the _T/_V/_S files of
    // one video segment with their own write seconds (Srikot: `..._131641_0072_V`
    // vs `..._131642_0072_T/_S`), so DJI names group by sequence + tolerance.
    const { key: groupKey, ms } = pairGroupKey(f.driveFile.name, /_[TVWZS]$/i);
    const key = `${pathPrefix}\u0000${groupKey}`;
    const role = videoRole(f.driveFile.name);
    const slot = f.category === 'srt' ? SRT_SLOTS[role] : VIDEO_SLOTS[role];

    let list = clusters.get(key);
    if (!list) {
      list = [];
      clusters.set(key, list);
    }

    let cluster = findPairCluster(list, ms, (s) => s[slot] !== undefined);
    if (!cluster) {
      cluster = { set: { baseName: flightBaseName(f.driveFile.name), pathPrefix }, ms };
      list.push(cluster);
      out.push(cluster.set);
    }
    cluster.set[slot] = f;
    if (cluster.ms === null) cluster.ms = ms;
  }

  return out;
}

/**
 * Pick the SRT to use for a flight path's coordinates, in preference order
 * thermal → visual → zoom → screen. Returns undefined if the set has no SRT.
 */
export function primaryFlightSrt(set: FlightPathSet): CategorizedFile | undefined {
  return set.thermalSrt ?? set.visualSrt ?? set.zoomSrt ?? set.screenSrt;
}

// ── Photo-GPS Flight-Route Synthesis (no-SRT fallback) ──
//
// When a flight has video but no SRT telemetry, there are no GPS coordinates to
// draw a route from. These helpers reconstruct an *approximate* route from the
// EXIF GPS of the photos captured during that flight — ordered by DJI capture
// time and segmented from the videos by the monotonic `_NNNN` sequence number.

/**
 * Extract the DJI capture sequence number from a filename or a suffix-stripped
 * base name. DJI names look like `DJI_20260713163855_0001_T.mp4` — the trailing
 * `_0001` (before the optional `_T/_V/_W/_Z/_S/_D` role suffix and extension) is
 * the monotonic capture sequence. Returns null for names without one.
 */
export function djiSequence(name: string): number | null {
  const noExt = name.replace(/\.[^.]+$/, '');
  const noRole = noExt.replace(/_[TVWZSD]$/i, '');
  const m = /_(\d+)$/.exec(noRole);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Extract the DJI capture time from a filename's embedded 14-digit
 * `YYYYMMDDHHMMSS` block as epoch ms. Parsed as UTC — the value is only ever
 * used for relative ordering and offsets, so the zone is immaterial as long as
 * it is consistent. Returns null when no 14-digit block is present.
 */
export function djiCaptureMs(name: string): number | null {
  const m = /(?:^|[^0-9])(\d{14})(?:[^0-9]|$)/.exec(name);
  if (!m) return null;
  const s = m[1];
  const ms = Date.UTC(
    +s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8),
    +s.slice(8, 10), +s.slice(10, 12), +s.slice(12, 14),
  );
  return Number.isFinite(ms) ? ms : null;
}

/** Format an epoch-offset in ms as an SRT timestamp "HH:MM:SS,mmm". */
function msToSrtTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms));
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${pad(Math.floor(total / 3600000))}:` +
    `${pad(Math.floor((total % 3600000) / 60000))}:` +
    `${pad(Math.floor((total % 60000) / 1000))},` +
    `${pad(total % 1000, 3)}`
  );
}

/** A photo carrying EXIF GPS, used to synthesize an approximate flight route. */
export interface SynthPhotoPoint {
  lng: number;
  lat: number;
  alt?: number;
  /** EXIF capture date — fallback ordering/timing when the filename lacks a stamp. */
  captureDate?: string;
  /** Original filename — supplies the DJI sequence number and capture time. */
  name: string;
  /** Subfolder path the photo came from (matches `FlightPathSet.pathPrefix`). */
  pathPrefix?: string;
}

/**
 * Partition photos into flight segments by DJI sequence number.
 *
 * DJI records a video, then the photos captured during/after it, then the next
 * video, and so on — all sharing one monotonically increasing `_NNNN` sequence.
 * Each photo is assigned to the flight set with the greatest sequence ≤ the
 * photo's own sequence (the most recent preceding video) within the same
 * subfolder. Photos before the first video — or with no parseable sequence — are
 * left unassigned (they still import as standalone photo markers).
 *
 * The returned map is keyed by `${pathPrefix}${baseName}`, the same key
 * `matchFlightPaths` uses, so callers can look a set's photos up directly.
 */
export function assignPhotosToFlights(
  flightSets: FlightPathSet[],
  photos: SynthPhotoPoint[],
): Map<string, SynthPhotoPoint[]> {
  const result = new Map<string, SynthPhotoPoint[]>();
  const sets = flightSets
    .map((s) => ({
      key: `${s.pathPrefix}${s.baseName}`,
      pathPrefix: s.pathPrefix,
      seq: djiSequence(s.baseName),
    }))
    .filter((s): s is { key: string; pathPrefix: string; seq: number } => s.seq !== null)
    .sort((a, b) => a.seq - b.seq);

  for (const s of sets) result.set(s.key, []);
  if (sets.length === 0) return result;

  for (const p of photos) {
    const pseq = djiSequence(p.name);
    if (pseq === null) continue;
    const prefix = p.pathPrefix ?? '';
    let owner: (typeof sets)[number] | null = null;
    for (const s of sets) {
      if (s.pathPrefix !== prefix) continue;
      // `sets` is sorted ascending, so the last set whose seq ≤ pseq wins.
      if (s.seq <= pseq) owner = s;
    }
    if (owner) result.get(owner.key)!.push(p);
  }
  return result;
}

/**
 * Synthesize an approximate flight route from photo EXIF GPS for a flight with
 * no SRT telemetry. Points are ordered by capture time (filename 14-digit stamp
 * → EXIF `captureDate` → sequence number), joined into a polyline, and given
 * SRT-style relative timestamps so the flight-path popup's scrubber and video
 * sync behave exactly as they do for a real (SRT) route.
 *
 * `originMs` anchors the timeline — pass the video's start time so each photo
 * frame sits at its true offset into the video; defaults to the first photo's
 * time. Returns null when fewer than 2 points are available (nothing to draw).
 */
export function synthesizeFlightRoute(
  points: SynthPhotoPoint[],
  originMs?: number | null,
): { coordinates: [number, number, number][]; timestamps: string[]; flightStats: FlightPathStats } | null {
  if (points.length < 2) return null;

  const keyed = points.map((p) => {
    const fromName = djiCaptureMs(p.name);
    const fromExif = p.captureDate ? Date.parse(p.captureDate) : NaN;
    const ms = fromName ?? (Number.isFinite(fromExif) ? fromExif : null);
    return { p, ms, seq: djiSequence(p.name) };
  });

  keyed.sort((a, b) => {
    if (a.ms !== null && b.ms !== null && a.ms !== b.ms) return a.ms - b.ms;
    if (a.seq !== null && b.seq !== null && a.seq !== b.seq) return a.seq - b.seq;
    return a.p.name.localeCompare(b.p.name);
  });

  const coordinates = keyed.map(
    ({ p }) => [p.lng, p.lat, p.alt ?? 0] as [number, number, number],
  );

  const haveTimes = keyed.every((k) => k.ms !== null);
  let timestamps: string[];
  let durationSec: number;
  if (haveTimes) {
    const first = keyed[0].ms as number;
    const last = keyed[keyed.length - 1].ms as number;
    const t0 = originMs != null && Number.isFinite(originMs) ? originMs : first;
    timestamps = keyed.map((k) => msToSrtTime((k.ms as number) - t0));
    durationSec = Math.round((last - first) / 1000);
  } else {
    // No parseable times → evenly space frames 1s apart so the scrubber/video
    // still animate through the points.
    timestamps = keyed.map((_, i) => msToSrtTime(i * 1000));
    durationSec = coordinates.length - 1;
  }

  const alts = keyed
    .map((k) => k.p.alt)
    .filter((a): a is number => typeof a === 'number');
  const avgAltitude = alts.length
    ? alts.reduce((sum, a) => sum + a, 0) / alts.length
    : undefined;

  return {
    coordinates,
    timestamps,
    flightStats: { frameCount: coordinates.length, durationSec, avgAltitude },
  };
}

// ── Local (browser File) Photo Matching ──

export type LocalPhotoVariant = 'thermal' | 'visual' | 'zoom';

/**
 * Detect the thermal/visual/zoom variant of a LOCAL upload by filename suffix.
 *
 * Works on a bare filename instead of a Drive `DriveFile`. A name with no
 * recognized suffix defaults to `'thermal'` (the most common single-image case).
 *
 * `_V` (Matrice/Mavic "visual" pairs: T/V), `_W` (H20T/H30T/M30T "wide" RGB in
 * T/W/Z trios), and `_D` (visual camera on some modern consumer drones) all map
 * to `'visual'` — they are the same RGB pair slot. The Drive `categorizeFile`
 * maps `_V`/`_W`/`_D` the same way.
 */
export function categorizeLocalPhotoName(name: string): LocalPhotoVariant {
  const base = name.toUpperCase().replace(/\.[^.]+$/, '');
  if (base.endsWith('_V') || base.endsWith('_W') || base.endsWith('_D')) return 'visual';
  if (base.endsWith('_Z')) return 'zoom';
  // `_T` or no suffix → thermal (default)
  return 'thermal';
}

/** A matched set of local thermal/visual/zoom items sharing the same base name. */
export interface LocalPhotoSet<T> {
  baseName: string;
  thermal?: T;
  visual?: T;
  zoom?: T;
}

/**
 * Group local upload items (anything carrying a `.name`) into thermal/visual/zoom
 * sets by their shared base name — the local-upload analogue of `matchPhotoSets`.
 *
 * Unlike the Drive matcher there is no subfolder path to disambiguate (the
 * browser multi-file picker exposes none), so matching is by base name alone.
 * DJI-named files pair by capture sequence with split-second tolerance, same
 * as the Drive matcher (see `pairGroupKey`).
 *
 * Duplicate-base safety: if the variant slot for a base is already filled (e.g.
 * two `DJI_0001_T.JPG` selected from different folders), a NEW group is started
 * so no photo is ever silently dropped.
 */
export function matchLocalPhotoSets<T extends { name: string }>(
  items: T[]
): LocalPhotoSet<T>[] {
  const clusters = new Map<string, { set: LocalPhotoSet<T>; ms: number | null }[]>();
  const out: LocalPhotoSet<T>[] = [];

  for (const item of items) {
    const { key, ms } = pairGroupKey(item.name, /_[TVZWD]$/i);
    const variant = categorizeLocalPhotoName(item.name);

    let list = clusters.get(key);
    if (!list) {
      list = [];
      clusters.set(key, list);
    }

    let cluster = findPairCluster(list, ms, (s) => s[variant] !== undefined);
    if (!cluster) {
      cluster = { set: { baseName: photoBaseName(item.name) }, ms };
      list.push(cluster);
      out.push(cluster.set);
    }
    cluster.set[variant] = item;
    if (cluster.ms === null) cluster.ms = ms;
  }

  return out;
}

// ── Import Summary ──

export interface ImportSummary {
  photoSets: PhotoSet[];
  flightPaths: FlightPathSet[];
  geotiffs: CategorizedFile[];
  pdfs: CategorizedFile[];
  unknowns: CategorizedFile[];
  /** Total bytes that will be uploaded to R2 (thermal photos, GeoTIFF, PDFs) */
  r2Bytes: number;
  /** Total bytes that stay as Drive URLs (visual, zoom, videos) */
  driveByes: number;
  /** Number of files that need R2 upload */
  r2FileCount: number;
  /** Number of files that stay as Drive URLs */
  driveFileCount: number;
}

/**
 * The included files that will be sent through the backend import endpoint:
 * thermal photos, GeoTIFFs, PDFs (R2 upload), SRTs (parse) — plus the
 * visual/zoom ANCHOR of any photo set that has no thermal member, which gets
 * an EXIF-only PEEK (first 128KB, `peekDriveFileExif`) for GPS placement while
 * the file itself stays hosted on Drive. Nothing visual/zoom is ever uploaded
 * to R2.
 *
 * Single source of truth shared by `importFiles` and the modal's "N files to
 * process" label — keep them in agreement by only ever changing this function.
 */
export function filesToProcess(files: CategorizedFile[]): CategorizedFile[] {
  const included = files.filter((f) => f.included);
  const anchorIds = new Set<string>();
  for (const set of matchPhotoSets(included)) {
    if (!set.thermal) {
      const anchor = set.visual ?? set.zoom;
      if (anchor) anchorIds.add(anchor.driveFile.id);
    }
  }
  return included.filter(
    (f) =>
      f.category === 'thermal_photo' ||
      f.category === 'geotiff' ||
      f.category === 'pdf' ||
      f.category === 'srt' ||
      anchorIds.has(f.driveFile.id)
  );
}

/**
 * Build a summary of what will be imported.
 */
export function buildImportSummary(files: CategorizedFile[]): ImportSummary {
  const included = files.filter((f) => f.included);
  const photoSets = matchPhotoSets(included);
  const flightPaths = matchFlightPaths(included);
  const geotiffs = included.filter((f) => f.category === 'geotiff');
  const pdfs = included.filter((f) => f.category === 'pdf');
  const unknowns = included.filter((f) => f.category === 'unknown');

  let r2Bytes = 0;
  let driveByes = 0;
  let r2FileCount = 0;
  let driveFileCount = 0;

  for (const f of included) {
    const size = parseInt(f.driveFile.size, 10) || 0;
    if (f.category === 'thermal_photo' || f.category === 'geotiff' || f.category === 'pdf') {
      r2Bytes += size;
      r2FileCount++;
    } else if (f.category === 'srt') {
      // SRT: tiny text, not counted as R2
    } else {
      // Videos AND visual/zoom photos stay as Drive URLs — visual/zoom anchors
      // of thermal-less sets only get an EXIF PEEK (first 128KB), never an
      // R2 upload.
      driveByes += size;
      driveFileCount++;
    }
  }

  return { photoSets, flightPaths, geotiffs, pdfs, unknowns, r2Bytes, driveByes, r2FileCount, driveFileCount };
}

// ── Google Drive URL Builders ──

/**
 * Build a proxied URL for embedding Google Drive images in <img> tags.
 * Routes through our API to avoid CORS/redirect issues with direct Drive URLs.
 * `width` selects a server-side Drive thumbnail (~10x smaller than the
 * original; the proxy snaps it to a fixed ladder). 2000px is visually
 * indistinguishable in every current display context.
 */
export function driveThumbnailUrl(fileId: string, width = 2000): string {
  return `${API_BASE}/api/v2/drive-image/${fileId}?w=${width}`;
}

/**
 * Build a Google Drive video embed URL for <iframe>.
 */
export function driveVideoEmbedUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/preview`;
}

/**
 * Build a Google Drive direct link.
 */
export function driveDirectUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/view`;
}

// ── Already-Imported Detection (P1 import dedup, undo/history plan) ──

/** Drive-derived identity already present in the project, collected by
 *  `collectExistingMedia`. Photos/flights/SLDs match by Google Drive file ID;
 *  orthophotos/overlays don't round-trip a Drive ID through their entity
 *  tables, so they match by original filename instead. */
export interface ExistingProjectMedia {
  /** Google Drive file IDs referenced anywhere in the project. */
  driveIds: Set<string>;
  /** Original GeoTIFF filenames of existing orthophotos (e.g. "site.tif"). */
  orthoFileNames: Set<string>;
  /** Names (filename minus extension) of existing overlays + SLD diagrams. */
  documentNames: Set<string>;
}

/**
 * Extract a Google Drive file ID from any stored Drive-derived URL — the
 * in-app image proxy (`/api/v2/drive-image/<id>`) or a drive.google.com link
 * (`/file/d/<id>/view|preview`). Returns null for non-Drive URLs. This makes
 * dedup work retroactively for items imported before `driveFileId` stamping.
 */
export function parseDriveIdFromUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  let m = /\/api\/v2\/drive-image\/([\w-]+)/.exec(url);
  if (m) return m[1];
  m = /drive\.google\.com\/file\/d\/([\w-]+)/.exec(url);
  return m ? m[1] : null;
}

/**
 * Collect every Drive file ID (stamped fields + parsed legacy URLs) and
 * filename identity the project already holds. Structural param types keep
 * this util store-free (no import cycle with the Zustand stores).
 */
export function collectExistingMedia(project: {
  indications: Array<{ properties: {
    driveFileId?: string; visualDriveId?: string; zoomDriveId?: string;
    image?: string; visualImage?: string; zoomImage?: string;
  } }>;
  flightPaths: Array<{
    driveFileIds?: string[];
    thermalVideoUrl?: string; visualVideoUrl?: string;
    zoomVideoUrl?: string; screenVideoUrl?: string;
  }>;
  orthophotos: Array<{ fileName?: string }>;
  overlays: Array<{ name?: string }>;
  slds: Array<{ name?: string; driveFileId?: string }>;
}): ExistingProjectMedia {
  const driveIds = new Set<string>();
  const add = (id?: string | null) => { if (id) driveIds.add(id); };

  for (const ind of project.indications) {
    const p = ind.properties;
    add(p.driveFileId);
    add(p.visualDriveId);
    add(p.zoomDriveId);
    add(parseDriveIdFromUrl(p.image));
    add(parseDriveIdFromUrl(p.visualImage));
    add(parseDriveIdFromUrl(p.zoomImage));
  }
  for (const fp of project.flightPaths) {
    for (const id of fp.driveFileIds ?? []) add(id);
    add(parseDriveIdFromUrl(fp.thermalVideoUrl));
    add(parseDriveIdFromUrl(fp.visualVideoUrl));
    add(parseDriveIdFromUrl(fp.zoomVideoUrl));
    add(parseDriveIdFromUrl(fp.screenVideoUrl));
  }
  const orthoFileNames = new Set<string>();
  for (const op of project.orthophotos) {
    if (op.fileName) orthoFileNames.add(op.fileName);
  }
  const documentNames = new Set<string>();
  for (const ov of project.overlays) {
    if (ov.name) documentNames.add(ov.name);
  }
  for (const sld of project.slds) {
    if (sld.name) documentNames.add(sld.name);
    add(sld.driveFileId);
  }
  return { driveIds, orthoFileNames, documentNames };
}

/**
 * Flag scan results that are already in the project and default them to
 * excluded. Photo/flight sets are matched as WHOLE sets — if any member's
 * Drive ID is known, every member is flagged, because sets import atomically
 * and a partial re-import would still create a duplicate marker. GeoTIFFs
 * match by original filename and PDFs by name (minus extension) against
 * existing overlays/SLDs. Flags are advisory: the per-row checkbox and the
 * bulk "Include anyway" action can re-include any flagged file.
 */
export function markAlreadyImported(
  files: CategorizedFile[],
  existing: ExistingProjectMedia,
): { files: CategorizedFile[]; flaggedCount: number } {
  const flaggedIds = new Set<string>();
  const flagSet = (members: Array<CategorizedFile | undefined>) => {
    const present = members.filter((f): f is CategorizedFile => !!f);
    if (present.some((f) => existing.driveIds.has(f.driveFile.id))) {
      for (const f of present) flaggedIds.add(f.driveFile.id);
    }
  };

  for (const set of matchPhotoSets(files)) {
    flagSet([set.thermal, set.visual, set.zoom]);
  }
  for (const set of matchFlightPaths(files)) {
    flagSet([
      set.thermalSrt, set.visualSrt, set.zoomSrt, set.screenSrt,
      set.thermalVideo, set.visualVideo, set.zoomVideo, set.screenVideo,
    ]);
  }
  for (const f of files) {
    if (f.category === 'geotiff' && existing.orthoFileNames.has(f.driveFile.name)) {
      flaggedIds.add(f.driveFile.id);
    } else if (f.category === 'pdf') {
      const stem = f.driveFile.name.replace(/\.[^.]+$/, '');
      if (existing.documentNames.has(stem) || existing.driveIds.has(f.driveFile.id)) {
        flaggedIds.add(f.driveFile.id);
      }
    }
  }

  if (flaggedIds.size === 0) return { files, flaggedCount: 0 };
  return {
    files: files.map((f) =>
      flaggedIds.has(f.driveFile.id) ? { ...f, alreadyImported: true, included: false } : f
    ),
    flaggedCount: flaggedIds.size,
  };
}

// ── Import Orchestrator ──

const CONCURRENCY = 4;

/** Stagger worker startup so the first request warms DNS + the connection pool
 *  before its peers race the resolver — avoids a burst of ERR_NAME_NOT_RESOLVED
 *  on the first ~4 requests when the host is uncached. */
const STARTUP_STAGGER_MS = 120;

/** Retry schedule for transient network errors (300 ms, 1.2 s, 4.8 s). */
const RETRY_DELAYS_MS = [300, 1200, 4800];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

/** Retry only on apiFetch's wrapped network errors (status 0). 4xx/5xx from the
 *  server are real failures and surface immediately. */
function isTransientNetworkError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

async function importWithRetry(
  siteId: string,
  fileId: string,
  fileName: string,
  category: 'photo' | 'orthophoto' | 'overlay' | 'sld' | 'srt',
  exifOnly: boolean,
  signal?: AbortSignal
): Promise<DriveFileImportResult | DriveSrtResult | DriveExifResult> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (signal?.aborted) throw new Error('aborted');
    try {
      return exifOnly
        ? await peekDriveFileExif(siteId, fileId, fileName)
        : await importDriveFile(siteId, fileId, fileName, category);
    } catch (err) {
      lastErr = err;
      if (!isTransientNetworkError(err)) throw err;
      if (attempt === RETRY_DELAYS_MS.length) break;
      await sleep(RETRY_DELAYS_MS[attempt], signal);
    }
  }
  throw lastErr;
}

/**
 * Import all included files from a categorized file list.
 * Calls `onProgress` after each file completes.
 *
 * Returns the final progress with all results.
 */
export async function importFiles(
  siteId: string,
  files: CategorizedFile[],
  onProgress: (progress: ImportProgress) => void,
  abortSignal?: AbortSignal
): Promise<ImportProgress> {
  // Only process files that go to R2, need SRT parsing, or need an EXIF peek
  // (visual/zoom anchors of thermal-less photo sets — see filesToProcess).
  const toImport = filesToProcess(files);

  const progress: ImportProgress = {
    total: toImport.length,
    completed: 0,
    files: toImport.map((f) => ({ file: f, status: 'queued' as FileStatus })),
  };

  onProgress({ ...progress });

  // Process with concurrency limit. Workers are staggered by STARTUP_STAGGER_MS
  // each so the first request warms DNS/TLS before peers race it.
  const queue = [...progress.files];
  const active: Promise<void>[] = [];

  async function processNext(startDelayMs: number): Promise<void> {
    if (startDelayMs > 0) {
      await sleep(startDelayMs, abortSignal);
      if (abortSignal?.aborted) return;
    }
    while (queue.length > 0) {
      if (abortSignal?.aborted) return;

      const item = queue.shift()!;
      item.status = 'importing';
      progress.currentFile = item.file.driveFile.name;
      onProgress({ ...progress });

      try {
        const category = mapCategoryToApi(item.file);
        // Visual/zoom anchors get an EXIF-only peek — the photo stays on
        // Drive; only thermal/geotiff/pdf actually upload to R2.
        const exifOnly =
          item.file.category === 'visual_photo' || item.file.category === 'zoom_photo';
        const result = await importWithRetry(
          siteId,
          item.file.driveFile.id,
          item.file.driveFile.name,
          category,
          exifOnly,
          abortSignal
        );

        item.status = 'done';
        item.result = result;
      } catch (err) {
        // Don't mark as error if the user canceled mid-flight; the loop will
        // exit cleanly on the next iteration via the aborted-check above.
        if (abortSignal?.aborted) return;
        item.status = 'error';
        item.error = err instanceof Error ? err.message : 'Unknown error';
      }

      progress.completed++;
      onProgress({ ...progress });
    }
  }

  for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) {
    active.push(processNext(i * STARTUP_STAGGER_MS));
  }

  await Promise.all(active);

  progress.currentFile = undefined;
  onProgress({ ...progress });
  return progress;
}

/** Map our category enum to the API's category parameter */
function mapCategoryToApi(
  file: CategorizedFile
): 'photo' | 'orthophoto' | 'overlay' | 'sld' | 'srt' {
  switch (file.category) {
    case 'thermal_photo':
      return 'photo';
    case 'visual_photo':
    case 'zoom_photo':
      // Only reaches the backend as the anchor of a thermal-less set
      // (filesToProcess), and only as an EXIF-only peek (exifOnly=true in
      // importFiles) — never an R2 upload.
      return 'photo';
    case 'geotiff':
      return 'orthophoto';
    case 'pdf':
      return file.pdfPurpose === 'sld' ? 'sld' : 'overlay';
    case 'srt':
      return 'srt';
    default:
      return 'photo';
  }
}

// ── Utility: Format File Size ──

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}
